import './PreviewPromptPage.scss';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Loader from '../../../shared/components/common/loader/Loader';
import { FirmData } from '../../../shared/models/monitor/FirmData';
import Title from '../../../shared/components/title/Title';
import { useFilters } from '../../../shared/contexts/filters/FiltersContext';
import {
  getMonitorTypeByChatOptionsType,
  getPeriod,
  prepareAndGetChatType,
} from '../../../shared/helpers/StatsHelpers';
import { useTranslation } from 'react-i18next';
import { PROJECTS, QUESTIONS } from '../../../shared/routes/path';
import Stats from './components/stats/Stats';
import { PromptResponse } from '../../../shared/models/monitor/PromptResponse';
import { getKeywordsData } from '../../../shared/services/monitor/keywords/KeywordsService';
import { getPromptData } from '../../../shared/services/monitor/prompt/PromptService';
import { KeywordsDailyStatsResponse } from '../../../shared/models/monitor/keywords/stats/KeywordsDailyStatsResponse';
import { getKeywordsDailyStats } from '../../../shared/services/monitor/keywords/stats/KeywordsStatsService';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { ConfirmDialog } from 'primereact/confirmdialog';
import useExportPromptData from '../../../shared/hooks/prompt/preview/UseExportPromptData';
import useRemovePrompt from '../../../shared/hooks/prompt/UseRemovePrompt';
import { MonitorEventLogResponse } from '../../../shared/models/monitor/MonitorEventLogResponse';
import { getEvents } from '../../../shared/services/monitor/events/EventsService';
import { KeywordResponse } from '../../../shared/models/monitor/KeywordResponse';
import { SourceDataResponse } from '../../../shared/models/monitor/data/sources/SourceDataResponse';
import { getSourcesForMonitor } from '../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import useExport from '../../../shared/hooks/global/UseExport';
import useAddon from '../../../shared/hooks/global/UseAddon';
import { getTags } from '../../../shared/api/services/tags/TagsService';
import { TagsResponse } from '../../../shared/models/tags/TagsResponse';
import FilterPanel from '../../../shared/components/filter-panel/FilterPanel';
import MoreBtn from '../../../shared/components/common/more-btn/MoreBtn';
import { Button } from 'primereact/button';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import {
  MonitorDataParams,
  useGetMonitorData,
} from '../../../shared/api/hooks/monitor/data/UseGetMonitorData';
import {
  expandSelectedChatTypes,
  getMonitorTypesForChatType,
} from '../../../shared/helpers/MonitorTypeHelpers';

interface PreviewPromptPageProps {
  force: boolean;
}

// po wejściu na project ustawiamy maksymalny zakres dat jeśli data stworzenia jest starsza niż miesiąc zamieniamy na ostania -1 miesiąc
// po kliknięciu w monitor na projecie najpierw sprawdzamy ustawioną datę jeśli jest ona inna niż zakres podlegający pod monitor zamieniamy ją ale odkładamy gdzieś informacje jakie daty były ustawione wcześniej

const PreviewPromptPage: React.FC<PreviewPromptPageProps> = ({ force }) => {
  const { t } = useTranslation();
  const { projectId, id, type, period, chat } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { accountResponse } = useAccount();
  const { canExport } = useExport();
  const canExportLlmResponse = useAddon('EXPORT_LLM_RESPONSE');

  const monitorIdNumber = Number(id);

  const { exportStats, exportSources, exportAiAnswers } = useExportPromptData(monitorIdNumber);
  const {
    monitorPeriod,
    setMonitorPeriod,
    range,
    setSelectedFirm,
    selectedFirm,
    selectedChatType,
    setSelectedChatType,
    selectedChatTypes,
    dateRange,
  } = useFilters();
  const [searchParams] = useSearchParams();

  const hasExplicitModelFilter = searchParams.get('models') !== null;

  const filterMonitorTypes = useMemo(() => {
    if (!hasExplicitModelFilter || !selectedChatTypes || selectedChatTypes.length === 0) return undefined;
    const expanded = expandSelectedChatTypes(selectedChatTypes);
    const types = new Set<string>();
    for (const ct of Array.from(expanded)) {
      for (const mt of getMonitorTypesForChatType(ct as any)) {
        types.add(mt);
      }
    }
    return Array.from(types);
  }, [hasExplicitModelFilter, selectedChatTypes]);
  const [promptData, setPromptData] = useState<PromptResponse>();
  const [keywordResponse, setKeywordResponse] = useState<KeywordResponse[]>([]);
  const [keywordsDailyStatsData, setKeywordsDailyStatsData] =
    useState<KeywordsDailyStatsResponse | null>(null);
  const [keywordsDailyStatsWithoutAggregateData, setKeywordsDailyStatsWithoutAggregateData] =
    useState<KeywordsDailyStatsResponse | null>(null);
  const [sourcesData, setSourcesData] = useState<SourceDataResponse>({} as SourceDataResponse);
  const [tags, setTags] = useState<TagsResponse[]>([]);
  const [eventsData, setEventsData] = useState<MonitorEventLogResponse[] | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [monitorDataPage, setMonitorDataPage] = useState(1);
  const [monitorDataPageSize, setMonitorDataPageSize] = useState(10);

  const initialChatDateRef = useRef(new URLSearchParams(window.location.search).get('chatDate') || undefined);

  const monitorDataParams: MonitorDataParams | null = useMemo(() => {
    if (!promptData?.keyword || !range?.[0] || !range?.[1] || !monitorPeriod || !selectedChatType) {
      return null;
    }

    return {
      monitorId: monitorIdNumber,
      keyword: promptData.keyword,
      monitorPeriod,
      chatType: getMonitorTypeByChatOptionsType(selectedChatType),
      from: range[0],
      to: range[1],
      page: monitorDataPage,
      pageSize: monitorDataPageSize,
      forDate: initialChatDateRef.current,
      models: filterMonitorTypes,
    };
  }, [
    promptData?.keyword,
    range,
    monitorPeriod,
    selectedChatType,
    monitorIdNumber,
    monitorDataPage,
    monitorDataPageSize,
    filterMonitorTypes,
  ]);

  const { monitorData, isLoading: isMonitorDataLoading } = useGetMonitorData(monitorDataParams);

  useEffect(() => {
    if (monitorData && initialChatDateRef.current) {
      initialChatDateRef.current = undefined;
    }
  }, [monitorData]);

  const [loading, setLoading] = useState<boolean>(true);

  const isFetchingRef = useRef(false);
  const { confirmRemove } = useRemovePrompt({
    refreshAfterDelete: () => {
      navigate(projectId ? PROJECTS : QUESTIONS);
      setLoading(false);
    },
    loader: () => setLoading(true),
  });

  useEffect(() => {
    const newMonitorPeriod = getPeriod(period);
    if (newMonitorPeriod !== monitorPeriod) {
      setMonitorPeriod(newMonitorPeriod);
    }

    const newChatType = prepareAndGetChatType(accountResponse, 'ALL', chat);
    if (newChatType !== selectedChatType) {
      setSelectedChatType(newChatType);
    }
  }, [type, period, chat, monitorPeriod, selectedChatType]);

  const prevStateRef = useRef<string>(JSON.stringify(''));

  useEffect(() => {
    if (!id || isNaN(Number(id))) {
      console.error('Id cannot be empty');
      return;
    }

    if (!range || !range[1] || (force && monitorPeriod === null)) {
      return;
    }

    const currentState = JSON.stringify({ monitorPeriod, range, selectedFirm, selectedChatType, filterMonitorTypes, id });
    if (currentState !== prevStateRef.current) {
      if (!isFetchingRef.current) {
        isFetchingRef.current = true;
        refresh(id);
        prevStateRef.current = currentState;
      }
    }
  }, [monitorPeriod, range, id, selectedFirm, selectedChatType, filterMonitorTypes, force]);

  useEffect(() => {
    return () => {
      prevStateRef.current = '';
    };
  }, []);

  const canEdit = !promptData?.demoVersion || (accountResponse !== null && accountResponse.isSuperAdmin);

  useEffect(() => {
    setSelectedChatType(prepareAndGetChatType(accountResponse, 'ALL', chat));
  }, []);

  const refresh = useCallback(
    (id: string | undefined) => {
      isFetchingRef.current = false;

      if (!monitorPeriod && !selectedChatType) {
        setMonitorPeriod('DAILY');
        setSelectedChatType('GPT');
        return;
      }

      if (!id || !Number(id) || !monitorPeriod || !selectedChatType) {
        const url = force ? `${PROJECTS}/${projectId}${QUESTIONS}` : QUESTIONS;
        navigate(url);
        return;
      }

      const from = range ? range[0] : null;
      const to = range ? range[1] : null;
      setLoading(true);

      Promise.all([
        getPromptData(id),
        getKeywordsData(id),
        getKeywordsDailyStats(
          id,
          monitorPeriod,
          getMonitorTypeByChatOptionsType(selectedChatType),
          from,
          to,
          filterMonitorTypes
        ),
        getEvents(Number(projectId), from, to, monitorPeriod, selectedChatType),
        getTags(Number(projectId)),
      ])
        .then(
          ([
            promptResponse,
            keywordsResponse,
            keywordsDailyStatsResponse,
            monitorEventsResponse,
            tagsResponse,
          ]) => {
            if (!promptResponse || promptResponse.status === 'PENDING') {
              navigate(QUESTIONS);
              return;
            }

            setPromptData(promptResponse);
            setEventsData(monitorEventsResponse);
            setTags(tagsResponse);

            let keywordsList: FirmData[] = keywordsResponse.map((keywordData) => ({
              name: keywordData.keyword,
              type: keywordData.normalizedKeyword,
            }));

            if (canEdit) {
              keywordsList.unshift({
                name: 'Add new',
                type: 'new',
              });
            }

            setKeywordResponse(keywordsResponse);
            setKeywordsDailyStatsData(keywordsDailyStatsResponse.keywordsDailyStatsData);
            setKeywordsDailyStatsWithoutAggregateData(
              keywordsDailyStatsResponse.keywordsDailyStatsWithoutAggregateData
            );

            const normalizedKeyword = promptResponse.keyword;
            if (normalizedKeyword && !selectedFirm) {
              const currentKeyword = keywordsList.find((keyword) => keyword.type === normalizedKeyword);
              if (currentKeyword) {
                setSelectedFirm({ name: currentKeyword.name, type: normalizedKeyword });
              }
            }

            getSourcesForMonitor(
              id,
              normalizedKeyword,
              getMonitorTypeByChatOptionsType(selectedChatType),
              from,
              to,
              filterMonitorTypes
            ).then((sourcesDataResponse) => {
              setSourcesData(sourcesDataResponse);
              setLoading(false);
            });
          }
        )
        .catch(() => navigate(QUESTIONS));
    },
    [monitorPeriod, range, selectedChatType, force, filterMonitorTypes]
  );

  if (loading || !promptData) {
    return (
      <div className=" page-content">
        <div className="page-box">
          <Loader />
        </div>
      </div>
    );
  }

  const suffix = promptData?.demoVersion ? ' (Demo)' : '';
  const promptName = promptData?.prompt + suffix;

  return promptData ? (
    <>
      {loading && <Loader />}
      <ConfirmDialog className="dialog-box" />
      <div className="preview-prompt-page page-content">
        <div className="page-box">
          <div className="page-box__filters" data-observe-sticky>
            <Title
              isBaseCategory={false}
              title={promptName}
              titleStyle={`ico--md  ${
                promptData?.localization ? `fi-${promptData.localization.toLowerCase()}` : 'ico--globe'
              } `}
              showStatusDot={true}
              children={
                <div className="filters">
                  <FilterPanel baseUrl={location.pathname} />
                  {(!promptData?.demoVersion ||
                    (accountResponse?.isSuperAdmin && !accountResponse?.isLimitedSuperAdmin)) && (
                    <MoreBtn rowId={0}>
                      <div className="buttons-list">
                        {accountResponse !== null &&
                          !['BLOCKED', 'DISABLED'].includes(accountResponse?.accountStatus) && (
                            <>
                              <Button
                                id={`btn-download-stats-${monitorIdNumber}`}
                                className={`action-btn material-symbols-outlined ${
                                  !canExport ? 'btn--disabled-tooltip' : ''
                                }`}
                                onClick={async () => {
                                  if (!isDownloading && canExport) {
                                    setIsDownloading(true);
                                    try {
                                      await exportStats();
                                    } finally {
                                      setIsDownloading(false);
                                    }
                                  }
                                }}
                              >
                                {isDownloading ? (
                                  <span className="ico--loader ico--xs" />
                                ) : (
                                  <span
                                    className={`ico--download ${
                                      !canExport ? 'ico--disabled' : ''
                                    } ico--xs`}
                                  />
                                )}
                              </Button>
                              <CustomTooltip
                                tooltipText={!canExport ? 'upgradePlanTrialExportMsg' : 'downloadStats'}
                                targetRef={`btn-download-stats-${monitorIdNumber}`}
                              />
                            </>
                          )}
                        <Button
                          id={`btn-remove-prompt-${monitorIdNumber}`}
                          className="action-btn material-symbols-outlined"
                          onClick={() => confirmRemove(monitorIdNumber)}
                        >
                          <span className="ico--trash ico--xs" />
                        </Button>
                        <CustomTooltip
                          tooltipText="removeThisQuestion"
                          targetRef={`btn-remove-prompt-${monitorIdNumber}`}
                        />
                      </div>
                    </MoreBtn>
                  )}
                </div>
              }
            />
          </div>

          <div className="page-box__stats">
            <Stats
              keywordResponse={keywordResponse}
              keywordsDailyStatsResponse={keywordsDailyStatsData}
              keywordsDailyStatsWithoutAggregateResponse={keywordsDailyStatsWithoutAggregateData}
              monitorData={monitorData}
              sourcesData={sourcesData}
              promptData={promptData}
              monitorEventsData={eventsData}
              exportSources={exportSources}
              exportAiAnswers={exportAiAnswers}
              canExport={canExport}
              canExportLlmResponse={canExportLlmResponse}
              tags={tags}
              onPageChange={(page, pageSize) => {
                initialChatDateRef.current = undefined;
                setMonitorDataPage(page);
                setMonitorDataPageSize(pageSize);
              }}
              pagination={monitorData?.pagination}
              isLoading={isMonitorDataLoading}
            />
          </div>
        </div>
      </div>
    </>
  ) : (
    <></>
  );
};

export default PreviewPromptPage;
